import { observer } from 'mobx-react-lite'
import { useEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useFeatureToggleStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'

/**
 * A route only some users can reach.
 *
 * `Feature` renders nothing when a toggle is off, which is right for a section
 * of a page and wrong for a whole one: somebody following a link would get a
 * blank screen with no way to tell it apart from a broken page. This sends them
 * home instead.
 *
 * Hiding the nav entry is not enough on its own. The link disappears but the
 * URL still works, so anyone who has bookmarked it, or been sent it, walks
 * straight in.
 */
export const FeatureRoute = observer(function FeatureRoute({ name, children }: { name: string; children: ReactNode }) {
  const store = useFeatureToggleStore()
  const enabled = useFeatureToggle(name)
  const wId = new URLSearchParams(useLocation().search).get(`wId`)

  useEffect(() => {
    bg(() => store.load())
  }, [store])

  // Nothing, not a redirect, until the toggles are in. Redirecting on the
  // default-false would eject users who do have the feature, every reload.
  if (!store.loaded) return null
  return enabled ? <>{children}</> : <Navigate to={wId ? `/?wId=${encodeURIComponent(wId)}` : `/`} replace />
})
